import { ReactNode, useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { FilterData, OPERATORS } from "../../types/filter"
import { Form, Formik, FormikProps } from "formik"
import { useDateFormatter } from "@react-aria/i18n"
import { formatBoolean, formatCpfCnpj } from "@/utils/format"
import { buildFilterRsql, buildFilterToUrl, parseFilterUrl } from "./buildFilterUrl"
import { FormikSelect, SelectItem } from "../ui/select"
import { Chip } from "../ui/chip"
import { FormikInputSearch } from "../ui/input"
import { Button } from "../ui/button"
import { cn } from "@/utils/cn"
import { LoaderCircle, Search } from "lucide-react"
import { Column } from "./DataTable"
import { PageRequest } from "@/types/pagination"
import { Sort } from "@/types/sort"

type FilterProps = {
    columns: Column[];
    onSearch?(filterRsql?: string, pagination?: PageRequest, sort?: Sort): void;
    loading?: boolean;
    defaultSort?: Sort;
}

export type Filters = Record<string, FilterData>;

const Filter = ({...props}: FilterProps) => {

    const [filters, setFilters] = useState<Filters>({});
    const navigate = useNavigate();
    const location = useLocation();
    const formatter = useDateFormatter({dateStyle: "short"});

    useEffect(() => {
        // deve ler a url e fazer a pesquisa no backend e atualizar a tabela
        if (location.search){
            const params = new URLSearchParams(location.search);
            const page = params.get('page');
            const sort = params.get('sort');

            let pageRequest: PageRequest|undefined = page ? {
                page: Number(page)-1,
                size: 20
            } : undefined;
            
            let sortObj: Sort|undefined = undefined;
            if (sort){
                const sortArr = sort.split('-');
                const fieldSort: Column | undefined = props.columns.find(f => f.label === decodeURI(sortArr[0]));
                sortObj = {sort: fieldSort?.field+","+sortArr[1]};
            } else if (props.defaultSort){
                sortObj = props.defaultSort;
            }
            let filter: Filters = parseFilterUrl(props.columns, location);
            let filterRsql = buildFilterRsql(filter);
            setFilters(filter);
            
            props.onSearch!(filterRsql, pageRequest, sortObj);
        } else {
            props.onSearch!(undefined, undefined, props.defaultSort);
        }
    }, [location.search])

    const addFilter = (selectedFilter: FilterData) => {
        const newFilters = selectedFilter.value ? {
            ...filters,
            [`${selectedFilter.column.label}_${selectedFilter.operator.name}`]: selectedFilter
          } : filters;
        setFilters(newFilters);
        navigate({search: buildFilterToUrl(newFilters, location.search)})
    }

    const removeFilter = (key: any) => {
        const newFitros = {...filters};
        delete newFitros[key];
        setFilters(newFitros);
        navigate({search: buildFilterToUrl(newFitros, location.search)})        
    }

    const clearFilters = () => {
        setFilters({});
        navigate({search: buildFilterToUrl({}, location.search)})
    }

    const getColumnByKey = (key: string|number): Column => {
        return props.columns.filter(col => col.field === key)[0];
    }

    const getLabelItemToShow = (value: FilterData): ReactNode => {
        let valueLabel: ReactNode = value.value;
        if (value.column.type === 'CPF_CNPJ'){
            valueLabel = formatCpfCnpj(value.value);
        } else if (value.column.type === 'DATE'){
            valueLabel = formatter.format(new Date(`${value.value}T03:00:00`));
        } else if (value.column.type === 'BOOLEAN'){
            valueLabel = formatBoolean(value.value);
        } else if (value.column.type === 'CUSTOM'){
            valueLabel = value.column.getValue!(value.value)
        }
        return <><span className="text-muted-foreground">{value.column.label} {value.operator.name.toLowerCase()}</span> <span className="font-medium">{valueLabel}</span></>
    }

    const getColumnsToFilter = () => {
        return props.columns.filter(col => col.filter);
    }

    const filterEntries = Object.entries(filters);

    return(
        <div className="space-y-3 border-b p-3">
            <Formik
                initialValues={{ filtro: '', column: getColumnsToFilter().shift()!.field, operator: OPERATORS['STRING'][0].name }}
                onSubmit={(values, actions) => {
                    let selectedColumn = getColumnByKey(values.column);
                    addFilter({
                        column: selectedColumn,
                        operator: (OPERATORS[selectedColumn?.type || 'STRING']).filter(op => op.name === values.operator)[0],
                        value: values.filtro
                    });
                    actions.resetForm({ values: { ...values, filtro: '' } });
                    actions.setSubmitting(false);
                }}
                >
                {(formikProps: FormikProps<any>) => (
                    <Form role="search">
                        <div className={cn(
                            "flex flex-col rounded-md border bg-background shadow-sm transition-shadow focus-within:ring-1 focus-within:ring-ring sm:h-9 sm:flex-row sm:items-center",
                            "[&_button]:border-0 [&_button]:shadow-none [&_button:focus]:ring-0 [&_input]:border-0 [&_input]:shadow-none [&_input:focus-visible]:ring-0"
                        )}>
                            <div className="flex items-center max-sm:border-b sm:w-48 sm:shrink-0">
                                <Search className="ml-3 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                                <div className="flex-1 font-medium">
                                    <FormikSelect name="column">
                                        {getColumnsToFilter().map((col) => (
                                            <SelectItem key={col.field} value={col.field} onClick={() => formikProps.setFieldValue('operator', OPERATORS[getColumnByKey(col.field)?.type || 'STRING'][0].name)}>
                                                {col.label}
                                            </SelectItem>
                                        ))}
                                    </FormikSelect>
                                </div>
                            </div>
                            <div className="text-muted-foreground max-sm:border-b sm:w-40 sm:shrink-0 sm:border-l">
                                <FormikSelect name="operator">
                                    {(OPERATORS[getColumnByKey(formikProps.values.column)?.type || 'STRING']).map((op) => (
                                        <SelectItem key={op.name} value={op.name}>
                                            {op.name}
                                        </SelectItem>
                                    ))}
                                </FormikSelect>
                            </div>
                            <div className="flex min-w-0 flex-1 items-center sm:border-l">
                                <div className="min-w-0 flex-1">
                                    <FormikInputSearch
                                        name='filtro'
                                        placeholder={`Buscar por ${(getColumnByKey(formikProps.values.column)?.label || '').toLowerCase()}`}
                                        type={getColumnByKey(formikProps.values.column)?.type!}
                                        value={formikProps.values.filtro}
                                        handleChange={(value: any) => formikProps.setFieldValue('filtro', value)}
                                    />
                                </div>
                                <Button type="submit" variant="ghost" size="sm" className="mr-1 h-7 shrink-0 text-muted-foreground hover:text-foreground" disabled={props.loading}>
                                    {props.loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : 'Filtrar'}
                                </Button>
                            </div>
                        </div>
                    </Form>
                    )}
            </Formik>
            {filterEntries.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                    {filterEntries.map(([key, filter]: [string, FilterData]) => ( 
                        <Chip key={key} onClose={() => removeFilter(key)}>
                            {getLabelItemToShow(filter)}
                        </Chip>
                    ))}
                    {filterEntries.length > 1 && (
                        <button type="button" onClick={clearFilters} className="rounded px-1 text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                            Limpar filtros
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

export default Filter;
